import { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Phone, Clock, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAP_SRC =
  "https://www.google.com/maps?q=Shop%2010%2F2605%20Beaudesert%20Rd%2C%20Calamvale%20QLD%204116&output=embed";

const fadeUp = {
  hidden: { opacity: 0, y: 36 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.1, duration: 0.75, ease: [0.22, 1, 0.36, 1] },
  }),
};

const inputCls =
  "w-full bg-cream border border-navy/15 rounded-xl px-5 py-4 text-ink placeholder:text-ink/40 focus:outline-none focus:border-gold focus:ring-2 focus:ring-gold/25 transition-[border-color,box-shadow] duration-300 font-body";

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thank you! We've received your message and will be in touch soon.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please call us at +61 408 382 360.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="bg-cream py-24 sm:py-36">
      <div className="max-w-7xl mx-auto px-6 sm:px-10">
        <motion.p
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          custom={0}
          className="font-script text-spice text-2xl sm:text-3xl mb-3"
        >
          find us
        </motion.p>
        <motion.h2
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          custom={1}
          className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-navy"
        >
          Come hungry. Leave happy.
        </motion.h2>

        <div className="mt-16 grid lg:grid-cols-12 gap-10">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            custom={2}
            className="lg:col-span-5 flex flex-col gap-8"
          >
            <div className="flex flex-col gap-6">
              <div className="flex items-start gap-4">
                <span className="mt-1 flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-navy text-gold">
                  <MapPin size={19} />
                </span>
                <div>
                  <p className="text-xs tracking-[0.25em] uppercase text-ink/50">Address</p>
                  <p className="mt-1 font-heading text-xl font-semibold text-navy" data-testid="contact-address">
                    Shop 10/2605 Beaudesert Rd,<br />Calamvale QLD 4116
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <span className="mt-1 flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-navy text-gold">
                  <Phone size={19} />
                </span>
                <div>
                  <p className="text-xs tracking-[0.25em] uppercase text-ink/50">Phone</p>
                  <a
                    data-testid="contact-phone-link"
                    href="tel:+61408382360"
                    className="mt-1 block font-heading text-xl font-semibold text-navy hover:text-spice transition-colors duration-300"
                  >
                    +61 408 382 360
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <span className="mt-1 flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-navy text-gold">
                  <Clock size={19} />
                </span>
                <div>
                  <p className="text-xs tracking-[0.25em] uppercase text-ink/50">Open</p>
                  <p className="mt-1 font-heading text-xl font-semibold text-navy">
                    7 Days — Lunch &amp; Dinner
                  </p>
                  <p className="text-sm text-ink/60 font-light">Dine in · Takeaway · Delivery</p>
                </div>
              </div>
            </div>

            <div className="rounded-2xl overflow-hidden border border-navy/10 shadow-lg h-[280px]">
              <iframe
                data-testid="contact-map"
                title="Kababish Calamvale location map"
                src={MAP_SRC}
                className="h-full w-full border-0"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </div>
          </motion.div>

          <motion.form
            data-testid="contact-form"
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            custom={3}
            onSubmit={submit}
            className="lg:col-span-7 bg-beige border border-navy/10 rounded-3xl p-8 sm:p-12 flex flex-col gap-5"
          >
            <h3 className="font-heading text-3xl font-semibold text-navy tracking-tight">
              Send us a message
            </h3>
            <div className="grid sm:grid-cols-2 gap-5">
              <input
                data-testid="contact-form-name"
                required
                placeholder="Your name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className={inputCls}
              />
              <input
                data-testid="contact-form-email"
                required
                type="email"
                placeholder="Email address"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className={inputCls}
              />
            </div>
            <input
              data-testid="contact-form-phone"
              placeholder="Phone (optional)"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              className={inputCls}
            />
            <textarea
              data-testid="contact-form-message"
              required
              rows={5}
              placeholder="How can we help? Bookings, catering, feedback..."
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              className={`${inputCls} resize-none`}
            />
            <motion.button
              data-testid="contact-form-submit-btn"
              type="submit"
              disabled={sending}
              whileHover={{ y: -2, boxShadow: "0 12px 36px rgba(198,40,40,0.4)" }}
              whileTap={{ scale: 0.97 }}
              className="mt-2 flex items-center justify-center gap-2.5 bg-spice text-cream font-semibold text-lg px-8 py-4 rounded-full disabled:opacity-60"
            >
              {sending ? <Loader2 size={19} className="animate-spin" /> : <Send size={18} />}
              {sending ? "Sending..." : "Send Message"}
            </motion.button>
          </motion.form>
        </div>
      </div>
    </section>
  );
};

export default Contact;
